import React from 'react';
import { FormFieldDef, FormFieldOption, FormSection } from '../types/formSchema';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';

/** Options for a field that draws them from a server-served vocabulary. */
function useSourcedOptions(source: FormFieldDef['optionsSource']): {
  options: FormFieldOption[];
  loaded: boolean;
  error: string | null;
} {
  const equipmentTypes = useEquipmentTypes();
  if (source !== 'equipment_types') return { options: [], loaded: true, error: null };
  return {
    options: equipmentTypes.types.map((item) => ({ value: item.key, label: item.label })),
    loaded: equipmentTypes.loaded,
    error: equipmentTypes.error,
  };
}

export interface CustomRendererProps {
  field: FormFieldDef;
  value: any;
  onChange: (value: any) => void;
  values: Record<string, any>;
  readOnly?: boolean;
}

export type CustomRendererMap = Record<string, React.FC<CustomRendererProps>>;

interface Props {
  sections: FormSection[];
  values: Record<string, any>;
  setField: (key: string, value: any) => void;
  customRenderers?: CustomRendererMap;
  readOnly?: boolean;
}

function isFieldVisible(field: FormFieldDef, values: Record<string, any>): boolean {
  if (!field.visibleWhen) return true;
  const { field: depField, equals } = field.visibleWhen;
  const val = values[depField] ?? '';
  if (Array.isArray(equals)) return equals.includes(val);
  return val === equals;
}

function GenericField({ field, value, onChange, readOnly }: { field: FormFieldDef; value: any; onChange: (v: any) => void; readOnly?: boolean }) {
  const label = `${field.label}${field.required ? ' (required)' : ' (optional)'}`;
  const sourced = useSourcedOptions(field.optionsSource);

  switch (field.type) {
    case 'select': {
      if (field.optionsSource) {
        if (sourced.error) {
          // The vocabulary is unreachable. The API skips its check in exactly
          // this case, so keep the field editable rather than leaving a
          // disabled control the reviewer cannot satisfy.
          return (
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>{label}</div>
              <input
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                required={field.required}
                disabled={readOnly}
                style={textInputStyle}
              />
              <div style={{ ...helpStyle, color: '#b91c1c' }}>
                {sourced.error}. Type the equipment type instead.
              </div>
            </div>
          );
        }
        // A stored value outside the reviewed vocabulary shows as no selection,
        // so `required` makes the reviewer classify it before saving.
        const isUnreviewed =
          sourced.loaded && Boolean(value) && !sourced.options.some((o) => o.value === value);
        return (
          <div style={fieldWrapStyle}>
            <div style={fieldLabelStyle}>{label}</div>
            <select
              value={isUnreviewed ? '' : (value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              required={field.required}
              disabled={readOnly || !sourced.loaded}
              style={selectInputStyle}
            >
              <option value="" disabled>
                {sourced.loaded ? 'Select an equipment type' : 'Loading…'}
              </option>
              {sourced.options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {isUnreviewed && (
              <div style={{ ...helpStyle, color: '#b45309' }}>
                Recorded as “{value}”, which is not in the reviewed list. Choose the
                reviewed type to save this equipment.
              </div>
            )}
            {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
          </div>
        );
      }
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{label}</div>
          <select
            value={value ?? (field.options?.[0]?.value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            disabled={readOnly}
            style={selectInputStyle}
          >
            {(field.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </div>
      );
    }

    case 'radio':
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{label}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(field.options || []).map((opt) => (
              <label key={opt.value} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: readOnly ? 'not-allowed' : 'pointer' }}>
                <input
                  type="radio"
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                  disabled={readOnly}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </div>
      );

    case 'textarea':
      return (
        <label style={{ ...fieldWrapStyle, display: 'block', fontSize: 12 }}>
          {label}
          <textarea
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={2}
            readOnly={readOnly}
            style={textareaStyle}
          />
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </label>
      );

    case 'checkbox':
      return (
        <div style={fieldWrapStyle}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={readOnly}
            />
            {label}
          </label>
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </div>
      );

    case 'number':
      return (
        <label style={{ ...fieldWrapStyle, display: 'block', fontSize: 12 }}>
          {label}
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            placeholder={field.placeholder}
            required={field.required}
            disabled={readOnly}
            style={textInputStyle}
          />
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </label>
      );

    default:
      return (
        <label style={{ ...fieldWrapStyle, display: 'block', fontSize: 12 }}>
          {label}
          <input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            readOnly={readOnly}
            style={textInputStyle}
          />
          {field.helpText && <div style={helpStyle}>{field.helpText}</div>}
        </label>
      );
  }
}

export default function SchemaFieldRenderer({ sections, values, setField, customRenderers, readOnly }: Props) {
  return (
    <>
      {sections.map((section) => {
        const visibleFields = section.fields.filter((f) => isFieldVisible(f, values));
        if (visibleFields.length === 0) return null;

        return (
          <React.Fragment key={section.id}>
            {visibleFields.map((field) => {
              const CustomRenderer = customRenderers?.[field.key];
              if (CustomRenderer) {
                return (
                  <CustomRenderer
                    key={field.key}
                    field={field}
                    value={values[field.key]}
                    onChange={(v) => setField(field.key, v)}
                    values={values}
                    readOnly={readOnly}
                  />
                );
              }
              return (
                <GenericField
                  key={field.key}
                  field={field}
                  value={values[field.key]}
                  onChange={(v) => setField(field.key, v)}
                  readOnly={readOnly}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}

const fieldWrapStyle: React.CSSProperties = {
  marginBottom: 2,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  marginBottom: 4,
};

const textInputStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  marginTop: 4,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontSize: 12,
  boxSizing: 'border-box' as const,
};

const selectInputStyle: React.CSSProperties = {
  ...textInputStyle,
};

const textareaStyle: React.CSSProperties = {
  ...textInputStyle,
  resize: 'vertical' as const,
};

const helpStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#6b7280',
  marginTop: 4,
};
